import { FC, useState } from 'react';
import { Button } from 'react-daisyui';
import { useNavigate } from 'react-router-dom';
import useCreatePracticeSet from '@/api/useCreatePracticeSet';
import { useAuthContext } from '@/context/AuthContext';
import DailyLimitModal from '@/components/ui/DailyLimitModal/DailyLimitModal';
import { Database } from '@/_constants/database.types';

type QuestionCategory = Database['public']['Enums']['question_types'];

interface RetryQuestionButtonProps {
  sourceQuestionId: string;
  questionCategory: QuestionCategory;
  hasImages?: boolean; // For Section B questions
}

const RetryQuestionButton: FC<RetryQuestionButtonProps> = ({ sourceQuestionId, questionCategory, hasImages }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [limitError, setLimitError] = useState<string | null>(null);
  const createPracticeSet = useCreatePracticeSet();
  const { session } = useAuthContext();
  const navigate = useNavigate();

  const handleRetry = async () => {
    if (!session) return;

    setIsCreating(true);
    setLimitError(null); // Clear previous errors

    try {
      const result = await createPracticeSet.mutateAsync({
        authId: session.user.id,
        questionCategory: questionCategory,
        setSize: 1,
        retryQuestionId: sourceQuestionId,
        hasImages: hasImages,
      });

      navigate(`/practice-sets/${result.practice_set_id}`);
    } catch (error: unknown) {
      console.error('Failed to create retry practice set:', error);
      setIsCreating(false);

      // Extract error message
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create practice set attempt. Please try again.';

      setLimitError(errorMessage);
    }
  };

  return (
    <>
      <div className="flex justify-center">
        <Button
          size="md"
          className="bg-primary hover:bg-brand-hover-alt border-none text-white px-8"
          onClick={handleRetry}
          disabled={isCreating || createPracticeSet.isPending}
        >
          {isCreating || createPracticeSet.isPending ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Creating...
            </span>
          ) : (
            'Re-try this question'
          )}
        </Button>
      </div>

      {/* Usage Limit Error Modal */}
      {limitError && <DailyLimitModal errorMessage={limitError} onClose={() => setLimitError(null)} />}
    </>
  );
};

export default RetryQuestionButton;
